import { useAssetCondition, useConditionByType } from "@features/ax/hooks";
import { PageHeader } from "@shared/components/PageHeader";
import { StepHelpers } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Slider } from "@shared/ui/slider";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useAssetDetail, useUpdateAsset } from "../../hooks";

interface ApiConditionParameter {
  name: string;
  score: number;
  isActive: boolean;
  createdBy: string;
  description: string;
  createdDate: string;
  code: string;
  weightage: number;
}

interface ConditionParameter extends ApiConditionParameter {
  sliderValue: number;
  weightValue: number;
}

interface Step3ConditionAssessmentProps {
  editMode: boolean;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  allStepData?: Record<number, any>;
}

const Step3ConditionAssessment: React.FC<Step3ConditionAssessmentProps> = ({
  editMode,
  stepHelpers,
  currentStepIndex = 2,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
  allStepData,
}) => {
  const { id, action } = useParams();
  const { toast } = useToast();
  const initializationRef = useRef(false);
  const { data: assetDetail } = useAssetDetail(id);

  const {
    data: conditionData,
    isLoading: isConditionLoading,
    isError: isConditionError,
  } = useAssetCondition();

  const {
    data: conditionByType,
    isLoading: isConditionByTypeLoading,
    refetch,
  } = useConditionByType(
    assetDetail?.assetClassCode,
    assetDetail?.assetTypeCode
  );

  const [conditionParameters, setConditionParameters] = useState<
    ConditionParameter[]
  >([]);
  const [overallConditionScore, setOverallConditionScore] = useState(0);
  const [analysisResults, setAnalysisResults] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const assessmentScores = [
    { score: 5, text: "Excellent condition, minimal maintenance needed" },
    { score: 4, text: "Good condition, routine maintenance recommended" },
    { score: 3, text: "Fair condition, preventive maintenance required" },
    { score: 2, text: "Poor condition, corrective maintenance needed" },
    { score: 1, text: "Critical condition, immediate attention required" },
  ];

  const { mutateAsync: updateAsset, isPending: isUpdating } =
    useUpdateAsset(id);

  // Memoized helper functions to prevent unnecessary re-renders
  const getStepData = useCallback(() => {
    if (!stepHelpers) return {};
    try {
      return stepHelpers.getStepData(currentStepIndex) || {};
    } catch (error) {
      console.error("Failed to get step 3 data:", error);
      return {};
    }
  }, [stepHelpers, currentStepIndex]);

  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return {};
    try {
      return stepHelpers.getStepData(0) || {};
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return {};
    }
  }, [stepHelpers]);

  useEffect(() => {
    // if (initializationRef.current || !conditionData || isConditionLoading) {
    //   return;
    // }

    let mergedParameters: ConditionParameter[] = [];

    if (Array.isArray(conditionData)) {
      if (Array.isArray(assetDetail?.assetParameters)) {
        // Create map for faster lookup
        const assetParamMap = assetDetail.assetParameters.reduce(
          (map: any, param: any) => {
            map[param.parameter] = param;
            return map;
          },
          {}
        );

        // Merge condition data with asset detail values
        mergedParameters = conditionData.map(
          (conditionParam: ApiConditionParameter) => {
            const assetParam = assetParamMap[conditionParam.name];
            return {
              ...conditionParam,
              sliderValue: assetParam?.score ?? 3,
              weightValue: assetParam?.weightValue ?? conditionParam.score,
            };
          }
        );
      } else if (
        conditionByType &&
        conditionByType.length > 0 &&
        conditionByType[0].assetParameters
      ) {
        // Use conditionByType.result[0].assetParameters
        const conditionByTypeParams = conditionByType[0].assetParameters;

        if (Array.isArray(conditionByTypeParams)) {
          // Create map for faster lookup by parameter name
          const conditionByTypeParamMap = conditionByTypeParams.reduce(
            (map: any, param: any) => {
              map[param.parameter] = param;
              return map;
            },
            {}
          );

          // Merge condition data with conditionByType values
          mergedParameters = conditionData.map(
            (conditionParam: ApiConditionParameter) => {
              const matchedParam = conditionByTypeParamMap[conditionParam.name];
              return {
                ...conditionParam,
                sliderValue: matchedParam?.score ?? 3,
                weightValue: matchedParam?.weightValue ?? conditionParam.score,
              };
            }
          );
        }
      } else {
        mergedParameters = conditionData.map(
          (param: ApiConditionParameter) => ({
            ...param,
            sliderValue: 3,
            weightValue: param.score,
          })
        );
      }

      setConditionParameters(mergedParameters);
      initializationRef.current = true;

      // Try to restore saved data after initial setup
      setTimeout(() => {
        const savedStepData = getStepData();
        if (savedStepData && Object.keys(savedStepData).length > 0) {
          console.log("Restoring saved step data...");

          if (savedStepData.conditionParameters) {
            setConditionParameters(savedStepData.conditionParameters);
          }
          if (savedStepData.overallConditionScore !== undefined) {
            setOverallConditionScore(savedStepData.overallConditionScore);
          }
          if (savedStepData.analysisResults) {
            setAnalysisResults(savedStepData.analysisResults);
          }
        }
        setIsInitialized(true);
      }, 100);
    }
  }, [
    conditionData,
    assetDetail?.assetParameters,
    conditionByType,
    isConditionLoading,
    getStepData,
  ]);

  // Memoize calculations to prevent excessive recalculation
  const calculationResults = useMemo(() => {
    const activeParams = conditionParameters.filter((p) => p.isActive);

    if (activeParams.length === 0) {
      return { overallScore: 0, enabledCount: 0 };
    }

    const totalWeight = activeParams.reduce((sum, p) => sum + p.weightValue, 0);
    const weightedScore = activeParams.reduce(
      (sum, p) => sum + (p.sliderValue || 3) * p.weightValue,
      0
    );

    const averageScore = totalWeight > 0 ? weightedScore / totalWeight : 0;
    const conditionScore = Math.round(averageScore * 20);

    return { overallScore: conditionScore, enabledCount: activeParams.length };
  }, [conditionParameters]);

  // Update overall score when calculations change
  useEffect(() => {
    if (!isInitialized) return;

    const { overallScore } = calculationResults;

    if (overallConditionScore !== overallScore) {
      setOverallConditionScore(overallScore);

      // Debounce onDataChange calls
      const timeoutId = setTimeout(() => {
        const updatedData = {
          ...data,
          conditionParameters,
          overallConditionScore: overallScore,
          lastUpdated: new Date().toISOString(),
        };
        onDataChange?.(updatedData);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [
    calculationResults,
    isInitialized,
    conditionParameters,
    data,
    onDataChange,
    overallConditionScore,
  ]);

  // Optimized SliderComponent with local state management
  const SliderComponent = React.memo(
    ({
      param,
      onScoreChange,
    }: {
      param: ConditionParameter;
      onScoreChange: (code: string, score: number) => void;
    }) => {
      const [localValue, setLocalValue] = useState(param.sliderValue || 3);

      // Update local state when param changes
      useEffect(() => {
        setLocalValue(param.sliderValue || 3);
      }, [param.sliderValue]);

      const handleChange = useCallback(
        (newValue: number[]) => {
          const score = newValue[0];
          setLocalValue(score);
          onScoreChange(param.code, score);
        },
        [param.code, onScoreChange]
      );

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Value: {localValue}
            </span>
            <span className="text-sm text-gray-500">Max: 5</span>
          </div>
          <Slider
            min={1}
            max={5}
            step={1}
            value={[localValue]}
            onValueChange={handleChange}
            disabled={
              assetDetail.assetParameters &&
              Object.keys(assetDetail.assetParameters).length !== 0
            }
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Critical</span>
            <span>Poor</span>
            <span>Fair</span>
            <span>Good</span>
            <span>Excellent</span>
          </div>
        </div>
      );
    }
  );

  // Optimized update functions with debouncing
  const updateParameterScore = useCallback(
    (parameterCode: string, sliderValue: number) => {
      setConditionParameters((prev) =>
        prev.map((p) => (p.code === parameterCode ? { ...p, sliderValue } : p))
      );
    },
    []
  );

  const updateParameterWeight = useCallback(
    (parameterCode: string, weightValue: number) => {
      setConditionParameters((prev) =>
        prev.map((p) => (p.code === parameterCode ? { ...p, weightValue } : p))
      );
    },
    []
  );

  const toggleParameter = useCallback((parameterCode: string) => {
    setConditionParameters((prev) =>
      prev.map((p) =>
        p.code === parameterCode ? { ...p, isActive: !p.isActive } : p
      )
    );
  }, []);

  // Memoized weightage calculation
  const calculateWeightagePercentage = useCallback(
    (paramWeight: number): string => {
      const enabledParams = conditionParameters.filter((p) => p.isActive);
      const totalWeight = enabledParams.reduce(
        (sum, p) => sum + p.weightValue,
        0
      );
      return totalWeight > 0
        ? ((paramWeight / totalWeight) * 100).toFixed(1)
        : "0";
    },
    [conditionParameters]
  );

  const getConditionGrade = useCallback((score: number) => {
    if (score >= 80)
      return {
        grade: "Excellent",
        color: "text-green-700",
        bgColor: "bg-green-100",
      };
    if (score >= 60)
      return {
        grade: "Good",
        color: "text-lime-700",
        bgColor: "bg-lime-100",
      };
    if (score >= 40)
      return {
        grade: "Fair",
        color: "text-yellow-700",
        bgColor: "bg-yellow-100",
      };
    if (score >= 20)
      return {
        grade: "Poor",
        color: "text-orange-700",
        bgColor: "bg-orange-100",
      };
    return {
      grade: "Critical",
      color: "text-red-700",
      bgColor: "bg-red-100",
    };
  }, []);

  // Handle next step with proper error handling
  const handleNext = useCallback(async () => {
    clearValidationError?.();

    try {
      const step1Data = getStep1Data();

      const payload = {
        asset_parameters: conditionParameters.map(
          (param: ConditionParameter) => ({
            parameter: param.name,
            score: param.sliderValue,
            weightage: calculateWeightagePercentage(param.weightValue),
            weight_value: param.weightValue,
          })
        ),
        condition_scores: overallConditionScore,
      };

      const response = await updateAsset(payload);

      if (response) {
        const completedData = {
          ...data,
          conditionScore: overallConditionScore,
          conditionParameters,
          analysisResults,
          payload,
          assetDraftId: step1Data?.assetDraftId,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        };

        onDataChange?.(completedData);
        onNext?.();
      } else {
        setValidationError?.("Failed to update asset data. Please try again.");
      }
    } catch (error) {
      console.error("Failed to continue from step 3:", error);
      setValidationError?.(
        "An error occurred while processing. Please try again."
      );
    }
  }, [
    clearValidationError,
    getStep1Data,
    conditionParameters,
    calculateWeightagePercentage,
    overallConditionScore,
    updateAsset,
    data,
    analysisResults,
    onDataChange,
    onNext,
    setValidationError,
  ]);

  const handlePrevious = useCallback(() => {
    const currentData = {
      ...data,
      conditionScore: overallConditionScore,
      conditionParameters,
      analysisResults,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    };

    onDataChange?.(currentData);
    onPrevious?.();
  }, [
    data,
    overallConditionScore,
    conditionParameters,
    analysisResults,
    onDataChange,
    onPrevious,
  ]);

  const handleChooseFile = useCallback(async () => {
    toast({
      title: "This functionality is not available",
      variant: "default",
    });
  }, [toast]);

  // Memoize expensive calculations
  const conditionGrade = useMemo(
    () => getConditionGrade(overallConditionScore),
    [overallConditionScore, getConditionGrade]
  );

  const enabledParameters = useMemo(
    () => conditionParameters.filter((p) => p.isActive),
    [conditionParameters]
  );

  if (isConditionLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading condition parameters...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isConditionError) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">
              Failed to load condition parameters.
            </p>
            <button
              onClick={handlePrevious}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4">
      <div className="mb-3">
        <PageHeader
          title="Condition Assessment"
          subtitle="Evaluate the current physical condition of your asset to determine maintenance needs and lifecycle planning."
        />
      </div>

      <div className="space-y-8">
        {/* Assessment Parameters */}
        <div className="border-t border-gray-200 pt-8">
          <h3 className="text-lg font-medium mb-4">Assessment Parameters</h3>
          <p className="text-sm text-gray-600 mb-6">
            Review each parameter. Use the toggle to enable/disable parameters,
            and adjust scores (1-5) and weights as needed. Higher scores
            indicate better condition. Inactive parameters are shown but do not
            affect the overall score.
          </p>

          {conditionParameters.length > 0 ? (
            <div className="space-y-4">
              {conditionParameters.map((param) => (
                <div
                  key={param.code}
                  className={`p-4 border rounded-lg bg-white""
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className={"font-medium text-gray-900"}>
                          {param.name}
                        </h4>
                      </div>
                      <p className={"text-sm text-gray-600"}>
                        {param.description}
                      </p>
                    </div>
                  </div>

                  {param.isActive && (
                    <div className="space-y-4 mt-4">
                      <div>
                        {/* Score Section with Slider */}
                        <div className="lg:col-span-2 space-y-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            Condition Score (1-5)
                            <span className="text-lg font-semibold text-blue-600">
                              {param.sliderValue || 3}
                            </span>
                          </Label>
                          <SliderComponent
                            param={param}
                            onScoreChange={updateParameterScore}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No condition parameters available.
            </div>
          )}

          <div
            className={`p-6 rounded-lg border-2 ${conditionGrade.bgColor} mt-4`}
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-2">
                Overall Condition Score
              </h3>
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="text-4xl font-bold">
                  {overallConditionScore}
                </span>
                <span className="text-lg text-gray-500">/ 100</span>
                <Badge
                  className={`text-lg px-4 py-2 ${conditionGrade.color}`}
                  variant="outline"
                >
                  {conditionGrade.grade}
                </Badge>
              </div>
              <p className="text-sm text-gray-600">
                Based on {enabledParameters.length} active assessment parameters
                out of {conditionParameters.length} total. Adjust individual
                scores and weights below to refine the assessment.
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">
              Assessment Guidelines
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              {assessmentScores.map(({ score, text }) => (
                <li key={score}>
                  • <strong>Score {score}:</strong> {text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button
          onClick={handlePrevious}
          disabled={isUpdating}
          variant="outline"
        >
          Previous
        </Button>
        <Button onClick={handleNext} isLoading={isUpdating}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default Step3ConditionAssessment;
